import intersection from "lodash/intersection";
import Handsontable from "handsontable";
import { asMoneyString, convertTimeToTimestamp } from "../../../utils/formatters";

const years = [2021, 2022, 2023, 2024];
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const currencySymbols = ["€", "zł", "$"];

let currency = "";
function compareFunctionFactory(sortOrder) {
  return (value, nextValue) => {
    let values = [value, nextValue];
    values.forEach((val, index) => {
      if (typeof val === "number") {
        return (values[index] = val);
      }
      let formattedValue = val.replace(currency, "").replace(",", "");
      values[index] = Number(formattedValue);
    });
    let [a, b] = values;
    if (sortOrder === "asc") return a - b;
    else if (sortOrder === "desc") return b - a;
    return 0;
  };
}

const customSortersByCurrency = (columns, headers) => {
  let sorters = [];

  columns.forEach((column, idx) => {
    if (column?.numericFormat && column?.numericFormat?.pattern) {
      const pattern = column.numericFormat.pattern;
      if (currencySymbols.some((symbol) => pattern.includes(symbol))) {
        sorters.push({ column: headers[idx], compareFunctionFactory });
      }
    }
  });

  return sorters;
};

const compareByTimeFunctionFactory = (sortOrder) => {
  return (value, nextValue) => {
    let timestampA = convertTimeToTimestamp(value);
    let timestampB = convertTimeToTimestamp(nextValue);

    if (sortOrder === "asc") {
      return timestampA - timestampB;
    } else if (sortOrder === "desc") {
      return timestampB - timestampA;
    }

    return 0;
  };
};

const compareByDateWithEmptyCell = (sortOrder) => {
  return (value, nextValue) => {
    const sortDirection = sortOrder === "asc" ? 1 : -1;

    const isValueEmpty = value === "";
    const isNextValueEmpty = nextValue === "";

    if (isValueEmpty && isNextValueEmpty) return 0;
    if (isValueEmpty) return 1;
    if (isNextValueEmpty) return -1;

    const [month1, day1, year1] = value.split("/").map(Number);
    const [month2, day2, year2] = nextValue.split("/").map(Number);

    const date1 = new Date(year1, month1 - 1, day1);
    const date2 = new Date(year2, month2 - 1, day2);

    if (date1 > date2) return 1 * sortDirection;
    if (date1 < date2) return -1 * sortDirection;
    return 0;
  };
};

const customSortersByTime = [
  {
    column: "Sale Time",
    compareFunctionFactory: compareByTimeFunctionFactory
  },
  {
    column: "Class Time",
    compareFunctionFactory: compareByTimeFunctionFactory
  },
  {
    column: "Conversion Time",
    compareFunctionFactory: compareByTimeFunctionFactory
  },
  {
    column: "Check-In Time",
    compareFunctionFactory: compareByTimeFunctionFactory
  }
];

const getCustomSorters = (columns, headers) => {
  return [
    {
      column: "Days To Conversion",
      compareFunctionFactory: (sortOrder) => (value, nextValue) => {
        // This comparator uses the same compare style as
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#Description
        const day1 = +value.toString().split(" ")[0];
        const day2 = +nextValue.toString().split(" ")[0];
        const sortDirection = sortOrder === "asc" ? 1 : -1;

        if (day1 > day2) return 1 * sortDirection;
        if (day1 === day2) return 0 * sortDirection;
        if (day1 < day2) return -1 * sortDirection;
      }
    },
    {
      column: "Second Visit Date",
      compareFunctionFactory: compareByDateWithEmptyCell
    },
    {
      column: "Late Second Visit Date",
      compareFunctionFactory: compareByDateWithEmptyCell
    },
    ...customSortersByCurrency(columns, headers), //to the BE side
    ...customSortersByTime,
    ...createRevenueColumns(years, months).map((revenueColumn) => ({
      column: revenueColumn,
      compareFunctionFactory
    }))
  ];
};

function createRevenueColumns(years, months) {
  const revenueColumns = [];
  for (const year of years) {
    for (const month of months) {
      revenueColumns.push(`Revenue (${month}, ${year})`);
    }
  }
  return revenueColumns;
}

const customRenderers = [
  {
    column: "Customer Name",
    renderer: (instance, td, row, col, prop, value) => {
      const link = document.createElement("a");
      const text = document.createTextNode(value);

      Handsontable.dom.empty(td);

      const rowData = instance.getDataAtRow(row);

      link.setAttribute("href", rowData[col + 1]);
      link.setAttribute("target", "_client_profile");
      link.appendChild(text);
      td.appendChild(link);
      return td;
    }
  },

  {
    column: "Revenue",
    renderer: (instance, td, row, col, prop, value) => {
      const link = document.createElement("a");

      //TODO: Pass as prop currency value
      const text = document.createTextNode("$" + asMoneyString(value));

      Handsontable.dom.empty(td);

      const rowData = instance.getDataAtRow(row);

      link.setAttribute("href", rowData[col + 1]);
      link.setAttribute("target", "_blank");
      link.appendChild(text);
      td.appendChild(link);
      return td;
    }
  }
];

function mergeSorters(columns, colHeaders) {
  const customSorters = getCustomSorters(columns, colHeaders);
  const changedColumns = columns.slice();
  const sorterNames = customSorters.map((sorter) => sorter.column);
  if (intersection(sorterNames.sort(), colHeaders).length === 0) {
    return columns;
  }

  customSorters.forEach((sorter) => {
    const { column, compareFunctionFactory } = sorter;
    const colIndex = colHeaders.indexOf(column);

    if (colIndex !== -1) {
      extractCurrency(changedColumns[colIndex]);
      changedColumns[colIndex].columnSorting = { compareFunctionFactory };
    }
  });

  return changedColumns;
}
function extractCurrency(columnObj) {
  let pattern = columnObj?.numericFormat?.pattern;
  if (pattern) {
    currency = pattern.replace("0,0.00", "");
  }
}
function mergeRenderers(columns, colHeaders, linkColumns) {
  const changedColumns = columns.slice();
  const rendererNames = customRenderers.map((renderer) => renderer.column);
  if (intersection(rendererNames.sort(), linkColumns).length === 0) {
    return columns;
  }

  customRenderers.forEach((rendererConfig) => {
    const { column, renderer } = rendererConfig;
    const colIndex = colHeaders.indexOf(column);
    if (colIndex > -1) {
      changedColumns[colIndex].renderer = renderer;
    }
  });

  return changedColumns;
}

export default function columnCustomizer(colHeaders, columns, linkColumns) {
  if (columns.length === 0) return [];

  let changedColumns;
  changedColumns = mergeSorters(columns, colHeaders);
  changedColumns = mergeRenderers(changedColumns, colHeaders, linkColumns);

  return changedColumns;
}
